import { PIPELINE_STEPS, getPipelineIndex, statusDisplayLabel } from './matterPipeline.js';
import { installmentPlanLabel } from './installmentPlans.js';
import { disputeLabel } from './disputeTypes.js';
import { formatMatterMoney } from './matterCurrency.js';
import {
  loadPersistedSort,
  savePersistedSort,
  sortRowsByColumn,
} from './listColumnSort.js';

const STORAGE_KEY = 'demandjet.demandsListColumns';
const SORT_STORAGE_KEY = 'demandjet.demandsListSort';

export const DEMAND_LIST_COLUMNS = Object.freeze([
  {
    id: 'debtorName',
    label: 'Debtor',
    required: true,
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'demandId',
    label: 'Demand ID',
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'amount',
    label: 'Amount',
    defaultVisible: true,
    sortType: 'number',
  },
  {
    id: 'stage',
    label: 'Status',
    defaultVisible: true,
    sortType: 'stage',
  },
  {
    id: 'attorney',
    label: 'Attorney partner',
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'updatedAt',
    label: 'Last activity',
    defaultVisible: true,
    sortType: 'date',
  },
  {
    id: 'email',
    label: 'Email',
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'company',
    label: 'Company',
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'city',
    label: 'City',
    defaultVisible: false,
    sortType: 'string',
  },
  {
    id: 'state',
    label: 'State',
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'postal',
    label: 'Postal code',
    defaultVisible: false,
    sortType: 'string',
  },
  {
    id: 'street',
    label: 'Street',
    defaultVisible: false,
    sortType: 'string',
  },
  {
    id: 'invoiceId',
    label: 'Invoice ID',
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'tracking',
    label: 'Tracking #',
    defaultVisible: false,
    sortType: 'string',
  },
  {
    id: 'installment',
    label: 'Payment plan',
    defaultVisible: false,
    sortType: 'string',
  },
  {
    id: 'dispute',
    label: 'Dispute type',
    defaultVisible: false,
    sortType: 'string',
  },
  {
    id: 'origin',
    label: 'Entry source',
    defaultVisible: false,
    sortType: 'string',
  },
  {
    id: 'createdAt',
    label: 'Created',
    defaultVisible: false,
    sortType: 'date',
  },
]);

export const DEFAULT_SORT = Object.freeze({ columnId: 'date', direction: 'desc' });

export function sortFieldLabel(columnId) {
  if (columnId === 'date') return 'Date';
  return DEMAND_LIST_COLUMNS.find((c) => c.id === columnId)?.label || columnId;
}

export function isValidSortColumn(columnId) {
  return columnId === 'date' || DEMAND_LIST_COLUMNS.some((c) => c.id === columnId);
}

export function defaultVisibleColumnIds() {
  return DEMAND_LIST_COLUMNS.filter((c) => c.defaultVisible || c.required).map((c) => c.id);
}

export function loadVisibleColumnIds() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultVisibleColumnIds();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return defaultVisibleColumnIds();
    const allowed = new Set(DEMAND_LIST_COLUMNS.map((c) => c.id));
    const next = parsed.filter((id) => allowed.has(id));
    for (const col of DEMAND_LIST_COLUMNS) {
      if (col.required && !next.includes(col.id)) next.unshift(col.id);
    }
    return next.length ? next : defaultVisibleColumnIds();
  } catch {
    return defaultVisibleColumnIds();
  }
}

export function saveVisibleColumnIds(ids) {
  const allowed = new Set(DEMAND_LIST_COLUMNS.map((c) => c.id));
  const next = (Array.isArray(ids) ? ids : []).filter((id) => allowed.has(id));
  for (const col of DEMAND_LIST_COLUMNS) {
    if (col.required && !next.includes(col.id)) next.unshift(col.id);
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  return next;
}

export function loadListSort() {
  return loadPersistedSort(SORT_STORAGE_KEY, DEFAULT_SORT, isValidSortColumn);
}

export function saveListSort(sort) {
  return savePersistedSort(SORT_STORAGE_KEY, sort, DEFAULT_SORT, isValidSortColumn);
}

export function stageLabel(status) {
  return statusDisplayLabel(status);
}

export function formatListCurrency(value, currencyOrMatter = 'USD') {
  return formatMatterMoney(value, currencyOrMatter, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatListDateTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return String(value);
  }
}

export function formatListDateKey(value) {
  if (!value) return 'unknown';
  try {
    const d = new Date(value);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;
  } catch {
    return 'unknown';
  }
}

export function formatListDateLabel(value) {
  if (!value) return 'Unknown';
  try {
    const d = new Date(value);
    const today = new Date();
    const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startThat = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.round((startToday - startThat) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return 'Unknown';
  }
}

export function cellValue(matter, columnId) {
  switch (columnId) {
    case 'debtorName':
      return matter.Debtor_Name || matter.Debtor_Company || '—';
    case 'amount':
      return formatListCurrency(matter.Amount_Owed, matter);
    case 'stage':
      return stageLabel(matter.Status);
    case 'attorney':
      return matter.Attorney_Name || 'Unassigned';
    case 'updatedAt':
      return formatListDateTime(matter.Updated_At || matter.Created_At);
    case 'email':
      return matter.Debtor_Email || '—';
    case 'company':
      return matter.Debtor_Company || matter.Debtor_Name || '—';
    case 'city':
      return matter.Debtor_City || '—';
    case 'state':
      return matter.Debtor_State || '—';
    case 'postal':
      return matter.Debtor_Postal_Code || '—';
    case 'street':
      return matter.Debtor_Street || '—';
    case 'demandId':
      return matter.Demand_ID || '—';
    case 'invoiceId':
      return matter.External_Invoice_ID || '—';
    case 'tracking':
      return matter.Tracking_Number || '—';
    case 'installment':
      return installmentPlanLabel(matter.Installment_Plan) || '—';
    case 'dispute':
      return disputeLabel(matter.Dispute_Type) || '—';
    case 'origin':
      return matter.Entry_Tag || (matter.Origin === 'AUTOMATED' ? 'Automated' : 'Manual');
    case 'createdAt':
      return formatListDateTime(matter.Created_At);
    default:
      return '—';
  }
}

function sortRawValue(matter, columnId) {
  switch (columnId) {
    case 'debtorName':
      return String(matter.Debtor_Name || matter.Debtor_Company || '').toLowerCase();
    case 'amount':
      return Number(matter.Amount_Owed) || 0;
    case 'stage': {
      return getPipelineIndex(matter.Status);
    }
    case 'attorney':
      return String(matter.Attorney_Name || 'Unassigned').toLowerCase();
    case 'date':
    case 'updatedAt':
      return new Date(matter.Updated_At || matter.Created_At || 0).getTime();
    case 'email':
      return String(matter.Debtor_Email || '').toLowerCase();
    case 'company':
      return String(matter.Debtor_Company || matter.Debtor_Name || '').toLowerCase();
    case 'city':
      return String(matter.Debtor_City || '').toLowerCase();
    case 'state':
      return String(matter.Debtor_State || '').toLowerCase();
    case 'postal':
      return String(matter.Debtor_Postal_Code || '').toLowerCase();
    case 'street':
      return String(matter.Debtor_Street || '').toLowerCase();
    case 'demandId':
      return String(matter.Demand_ID || '').toLowerCase();
    case 'invoiceId':
      return String(matter.External_Invoice_ID || '').toLowerCase();
    case 'tracking':
      return String(matter.Tracking_Number || '').toLowerCase();
    case 'installment':
      return String(installmentPlanLabel(matter.Installment_Plan) || '').toLowerCase();
    case 'dispute':
      return String(disputeLabel(matter.Dispute_Type) || '').toLowerCase();
    case 'origin':
      return String(
        matter.Entry_Tag || (matter.Origin === 'AUTOMATED' ? 'Automated' : 'Manual')
      ).toLowerCase();
    case 'createdAt':
      return new Date(matter.Created_At || 0).getTime();
    default:
      return '';
  }
}

export function sortMatters(matters = [], sort = DEFAULT_SORT) {
  return sortRowsByColumn(
    matters,
    {
      columnId: sort?.columnId || DEFAULT_SORT.columnId,
      direction: sort?.direction,
    },
    sortRawValue,
    (matter) => String(matter?.ROWID || '')
  );
}

/** Build display rows with date tags when the calendar day changes. */
export function buildListRows(matters = [], sort = DEFAULT_SORT) {
  const sorted = sortMatters(matters, sort);
  let previousKey = null;
  return sorted.map((matter) => {
    const when = matter.Updated_At || matter.Created_At;
    const key = formatListDateKey(when);
    const showDateTag = key !== previousKey;
    previousKey = key;
    return {
      matter,
      dateKey: key,
      dateLabel: formatListDateLabel(when),
      showDateTag,
    };
  });
}

/** @deprecated Prefer buildListRows with explicit sort. */
export function groupMattersByActivityDate(matters = []) {
  const rows = buildListRows(matters, DEFAULT_SORT);
  const groups = [];
  const map = new Map();
  for (const row of rows) {
    if (!map.has(row.dateKey)) {
      const group = { key: row.dateKey, label: row.dateLabel, matters: [] };
      map.set(row.dateKey, group);
      groups.push(group);
    }
    map.get(row.dateKey).matters.push(row.matter);
  }
  return groups;
}
